import { use, useEffect, useState } from 'react';
import { FieldValues, useForm, UseFormReturn } from 'react-hook-form';
import ConfirmationPopupProviderContext from 'shared/components/confirmation-pop-up/providers/contexts/confirmation-popup-provider-context';
import ErrorMessageProviderContext from 'shared/components/error/providers/contexts/error-message-context';
import ViewingMode from 'shared/components/viewing-mode-controller/types/viewing-mode';

import { ActiveEditor } from 'features/feature-subscriptions/hooks/feature-subscriptions-editing';
import { useFeatureSubscriptionsEditingContext } from '../contexts/feature-subscriptions-editing-provider-context';
import { FeatureSubscriptionType } from '../models/feature-subscription-type';
import { LocationFeatureSubscription } from '../models/location-feature-subscription';
import { ReadLocationFeatureSubscriptionsResponse } from '../models/location-feature-subscription-response';
import { UpdateLocationFeatureSubscriptionsCommand } from '../models/update-location-feature-subscriptions-command';
import { useReadLocationFeatureSubscriptions, useUpdateLocationFeatureSubscriptions } from '../providers/feature-subscriptions-queries';

export type LocationFeatureSubscriptions = {
  selectedLocation: LocationFeatureSubscription | undefined;
  selectedItem: LocationFeatureSubscription | undefined;
  activeLocationFeatureSubscriptionTypes: Set<FeatureSubscriptionType>;
  activeEditor: ActiveEditor;
  detailsViewingMode: ViewingMode;
  locationForm: UseFormReturn<LocationFeatureSubscription>;
  readLocationFeatureSubscriptionsData: ReadLocationFeatureSubscriptionsResponse | undefined;
  readLocationFeatureSubscriptionsIsSuccess: boolean;
  readLocationFeatureSubscriptionsIsLoading: boolean;
  readLocationFeatureSubscriptionsIsFetching: boolean;
  updateLocationFeatureSubscriptionsIsPending: boolean;
  openDetails: (locationFeatureSubscription: LocationFeatureSubscription) => void;
  closeDetails: () => void;
  handleCloseDetails: () => void;
  handleDetailsEdit: () => void;
  handleDetailsCancel: () => void;
  submitLocationFeatureSubscriptions: (fieldValues: FieldValues) => void;
};

const useLocationFeatureSubscriptions = (): LocationFeatureSubscriptions => {
  const { setErrorMessage } = use(ErrorMessageProviderContext);
  const { showDefaultConfirmationPopUp, enablePopUpGuard } = use(ConfirmationPopupProviderContext);
  const { activeEditor, setActiveEditor } = useFeatureSubscriptionsEditingContext();

  const [selectedLocation, setSelectedLocation] = useState<LocationFeatureSubscription | undefined>(undefined);
  const [detailsViewingMode, setDetailsViewingMode] = useState<ViewingMode>('viewing');

  const locationForm = useForm<LocationFeatureSubscription>({
    defaultValues: { locationId: '', locationName: '', featureSubscriptions: [] },
    mode: 'onBlur',
  });

  const { reset: resetLocationForm } = locationForm;

  const {
    data: readLocationFeatureSubscriptionsData,
    isSuccess: readLocationFeatureSubscriptionsIsSuccess,
    isLoading: readLocationFeatureSubscriptionsIsLoading,
    isFetching: readLocationFeatureSubscriptionsIsFetching,
    error: readLocationFeatureSubscriptionsError,
  } = useReadLocationFeatureSubscriptions();

  const {
    mutate: updateLocationFeatureSubscriptions,
    error: updateLocationFeatureSubscriptionsError,
    isError: updateLocationFeatureSubscriptionsIsError,
    isPending: updateLocationFeatureSubscriptionsIsPending,
  } = useUpdateLocationFeatureSubscriptions();

  useEffect(() => {
    if (readLocationFeatureSubscriptionsError) {
      setErrorMessage(readLocationFeatureSubscriptionsError);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [readLocationFeatureSubscriptionsError]);

  useEffect(() => {
    if (updateLocationFeatureSubscriptionsIsError) {
      setErrorMessage(updateLocationFeatureSubscriptionsError);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateLocationFeatureSubscriptionsIsError]);

  useEffect(() => {
    if (selectedLocation) {
      resetLocationForm(selectedLocation);
    }
  }, [selectedLocation, resetLocationForm]);

  useEffect(() => {
    if (selectedLocation && readLocationFeatureSubscriptionsData) {
      const updatedLocation = readLocationFeatureSubscriptionsData.locationFeatureSubscriptions.find(
        (location) => location.locationId === selectedLocation.locationId,
      );

      if (updatedLocation) {
        setSelectedLocation(updatedLocation);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [readLocationFeatureSubscriptionsData]);

  useEffect(() => {
    enablePopUpGuard(locationForm.formState.isDirty);
  }, [locationForm.formState.isDirty]);

  const handleCloseDetails = (): void => {
    setSelectedLocation(undefined);
    setDetailsViewingMode('viewing');
    setActiveEditor('none');
    locationForm.reset();
  };

  const openDetails = (locationFeatureSubscription: LocationFeatureSubscription): void => {
    const isSameLocation = selectedLocation?.locationId === locationFeatureSubscription.locationId;
    showDefaultConfirmationPopUp({
      shouldShow: locationForm.formState.isDirty,
      action: () => {
        setSelectedLocation(isSameLocation ? undefined : locationFeatureSubscription);
        setDetailsViewingMode('viewing');
        setActiveEditor('none');
      },
    });
  };

  const closeDetails = (): void => {
    showDefaultConfirmationPopUp({
      shouldShow: locationForm.formState.isDirty,
      action: handleCloseDetails,
    });
  };

  const handleDetailsEdit = (): void => {
    setDetailsViewingMode('editing');
    setActiveEditor('location');
  };

  const handleDetailsCancel = (): void => {
    resetView();
  };

  const handleSubmitSucceeded = (): void => {
    resetView();
  };

  const resetView = (): void => {
    locationForm.reset();
    setDetailsViewingMode('viewing');
    setActiveEditor('none');
  };

  const submitLocationFeatureSubscriptions = (fieldValues: FieldValues): void => {
    const { locationId, featureSubscriptions } = fieldValues as LocationFeatureSubscription;
    const command: UpdateLocationFeatureSubscriptionsCommand = {
      locationFeatureSubscriptions: [
        {
          locationId,
          featureSubscriptions: featureSubscriptions.map((subscription) => ({
            featureSubscriptionType: subscription.featureSubscriptionType,
            isActive: subscription.isActive,
          })),
        },
      ],
    };
    updateLocationFeatureSubscriptions(command, {
      onSuccess: handleSubmitSucceeded,
    });
  };

  const selectedItem = readLocationFeatureSubscriptionsData?.locationFeatureSubscriptions.find(
    (location) => location.locationId === selectedLocation?.locationId,
  );

  const activeLocationFeatureSubscriptionTypes = new Set(
    readLocationFeatureSubscriptionsData?.locationFeatureSubscriptions
      .flatMap((location) => location.featureSubscriptions)
      .filter((subscription) => subscription.isActive)
      .map((subscription) => subscription.featureSubscriptionType) ?? [],
  );

  return {
    selectedLocation,
    selectedItem,
    activeLocationFeatureSubscriptionTypes,
    activeEditor,
    detailsViewingMode,
    locationForm,
    readLocationFeatureSubscriptionsData,
    readLocationFeatureSubscriptionsIsSuccess,
    readLocationFeatureSubscriptionsIsLoading,
    readLocationFeatureSubscriptionsIsFetching,
    updateLocationFeatureSubscriptionsIsPending,
    openDetails,
    closeDetails,
    handleCloseDetails,
    handleDetailsEdit,
    handleDetailsCancel,
    submitLocationFeatureSubscriptions,
  };
};

export default useLocationFeatureSubscriptions;
